import React, { useState, useEffect } from 'react'
import { X, Mail, AlertCircle, Copy, Check, Info } from 'lucide-react'
import { teamService } from './teamService'
import { DetailedRosterMember } from '../../types/team'

interface InviteAthleteModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (message: string) => void
  member: DetailedRosterMember | null
  organizationId: string
  teamId: string
}

export const InviteAthleteModal: React.FC<InviteAthleteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  member,
  organizationId,
  teamId,
}) => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!isOpen || !member) {
      setEmail('')
      setErrorMsg(null)
      setGeneratedToken(null)
      setCopied(false)
      return
    }

    setEmail(member.invitation_email || member.user_email || '')
    setErrorMsg(null)
    setGeneratedToken(null)
    setCopied(false)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, member, onClose])

  if (!isOpen || !member) return null

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!email.trim() || !email.includes('@')) {
      setErrorMsg('Introduce una dirección de correo electrónico válida.')
      return
    }

    setIsSubmitting(true)
    try {
      const res = await teamService.inviteUser({
        organizationId,
        teamId,
        email: email.trim().toLowerCase(),
        roles: ['athlete'],
        athleteId: member.athlete_id,
      })

      if (res.success) {
        setGeneratedToken(res.token)
        onSuccess(`Invitación generada para ${member.first_name} ${member.last_name} (${res.email}).`)
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al generar la invitación.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const activationUrl = generatedToken
    ? `${window.location.origin}/?token=${generatedToken}`
    : ''

  const handleCopyLink = () => {
    if (!activationUrl) return
    navigator.clipboard.writeText(activationUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="modal-content"
        style={{ maxWidth: '480px', width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Mail size={18} style={{ color: 'var(--brand-red)' }} />
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
              Invitar Atleta a Activar Cuenta
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Cerrar modal"
            style={{ padding: '0.25rem' }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          {errorMsg && (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--status-danger-bg)',
                color: 'var(--status-danger-text)',
                border: '1px solid var(--status-danger-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>{errorMsg}</div>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-main)' }}>
              {member.first_name} {member.last_name}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              Ficha deportiva vinculada: #{member.jersey_number ?? '-'} · {member.primary_position}
            </div>
          </div>

          {!generatedToken ? (
            <form onSubmit={handleSend}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Correo Electrónico de Destino *
                </label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="ejemplo@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p style={{ fontSize: '0.73rem', color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                  Al emitir la invitación, se generará un token criptográfico seguro de un solo uso con caducidad de 7 días. Si existía una invitación anterior para este jugador, quedará invalidada automáticamente.
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Generando...' : 'Generar Invitación'}
                </button>
              </div>
            </form>
          ) : (
            <div>
              <div
                style={{
                  padding: '0.85rem',
                  backgroundColor: 'var(--status-success-bg)',
                  color: 'var(--status-success-text)',
                  border: '1px solid var(--status-success-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <Check size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Invitación creada correctamente:</strong> El token está activo y vinculado a la ficha de {member.first_name} {member.last_name}.
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                    Enlace de activación para entrega manual:
                  </label>
                  <span className="badge badge-yellow" style={{ fontSize: '0.68rem' }}>
                    Entrega manual · SMTP no configurado
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    readOnly
                    value={activationUrl}
                    className="form-control"
                    style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCopyLink}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copied ? 'Copiado' : 'Copiar enlace'}</span>
                  </button>
                </div>
              </div>

              <div
                style={{
                  padding: '0.65rem 0.75rem',
                  backgroundColor: 'var(--surface-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--surface-border)',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.45rem',
                  marginBottom: '1.25rem',
                }}
              >
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--brand-blue)' }} />
                <div>
                  <strong>Canal de entrega operativo:</strong> La entrega automática por correo electrónico permanece pendiente de configuración de Custom SMTP. Debe copiar el enlace anterior y entregárselo directamente al deportista de forma manual para que cree su acceso y complete su perfil.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-primary" onClick={onClose}>
                  Entendido / Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
